import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listMembersApi } from '@/lib/api';
import type { ListMember } from '@/types';

export function useListMembers(listId: string | undefined) {
  return useQuery({
    queryKey: ['lists', listId, 'members'],
    queryFn: async () => {
      if (!listId) throw new Error('List ID required');
      const response = await listMembersApi.getMembers(listId);
      return response.data as ListMember[];
    },
    enabled: !!listId,
  });
}

export function useAddListMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ listId, userId }: { listId: string; userId: string }) => {
      const response = await listMembersApi.addMember(listId, userId);
      return response.data as ListMember;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lists', variables.listId, 'members'] });
    },
  });
}

export function useRemoveListMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ listId, userId }: { listId: string; userId: string }) => {
      await listMembersApi.removeMember(listId, userId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lists', variables.listId, 'members'] });
    },
  });
}

export function useInheritListFromProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (listId: string) => {
      await listMembersApi.inheritFromProject(listId);
    },
    onSuccess: (_, listId) => {
      queryClient.invalidateQueries({ queryKey: ['lists', listId, 'members'] });
    },
  });
}
